import { AxiosError } from 'axios';
import { PageProps, graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import AnalyticsPoint from 'gatsby-plugin-purina-analytics/AnalyticsPoint';
import { css } from '@linaria/core';
import { styled } from '@linaria/react';
import React from 'react';
import { Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getArticleSearchData } from 'src/ssr/getSearchData';
import { theme } from 'src/theme/theme';
import Layout from '../../components/Layout';
import Link from '../../components/Link';
import Seo from '../../components/Seo';
import Card from '../../components/card/Card';
import Filmstrip from '../../components/filmstrip/Filmstrip';
import RichText from '../../components/rich_text/RichText';
import {
  getHelmetPropsFromMetatagProps,
  getJsonFromMetatagProps,
} from '../../utils/metatagHelpers';
import ArticleListingBody from '../components/ArticleListingBody';
import { ArticleSD } from './types/types';

interface DataProps {
  node: IArticleListing;
  translations: Translations;
}

const customButtonStyles = css`
  box-sizing: border-box;
  display: flex;
  height: 100%;
  justify-content: center;
  justify-items: center;
`;

const CardContainer = styled.div`
  text-decoration: none;
  color: ${theme.light.text.default};

  a {
    color: ${theme.light.text.default};
    text-decoration: none;
  }

  :hover {
    color: ${theme.light.text.default};
    text-decoration: underline;
  }
`;

const StyledLink = styled(Link)`
  z-index: 1;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
  }
`;

interface FeaturedCardProps {
  node: NodeContent;
}

const FeaturedCard = ({ node }: FeaturedCardProps) => {
  // eslint-disable-next-line no-underscore-dangle
  const media = node?.relationships?.field_image?.relationships?.field_media_image?.gatsbyImage;
  const alt = node?.relationships?.field_image?.field_media_image?.alt || '';

  return (
    <CardContainer>
      <Card
        containment="implicit"
        media={media ? <GatsbyImage image={media} alt={alt} loading="eager" /> : undefined}
      >
        <div className="mt-2">
          <h3 className="h3 fw-bold">
            <StyledLink to={node.path.alias}>{node.title}</StyledLink>
          </h3>
          {node?.body?.summary && <p className="typography_body fw-light">{node.body.summary}</p>}
        </div>
      </Card>
    </CardContainer>
  );
};

const ArticleListingTemplate: React.FC<PageProps<DataProps, any, any, ArticleSD>> = function ({
  data: { node, translations },
  serverData,
}) {
  const { t } = useTranslation();

  const { featuredArticle, relatedListings } = node.relationships;
  const helmetProps = getHelmetPropsFromMetatagProps(node.metatag);
  const schemaMarkup = getJsonFromMetatagProps(node.metatag);
  return (
    <Layout
      node={node}
      metaData={node.metatag}
      language={node.langcode}
      translations={translations}
    >
      <Seo {...helmetProps} schemaMarkup={schemaMarkup} />
      <Container fluid className="bg-gray-100 py-5 text-center">
        <h1>{node.title}</h1>
        {node?.body?.processed && (
          <RichText className="text-center py-4" body={node.body.processed} />
        )}
      </Container>
      {featuredArticle && (
        <AnalyticsPoint type="module" node={featuredArticle} as={Container} className="pt-5">
          <FeaturedCard node={featuredArticle} />
        </AnalyticsPoint>
      )}
      <AnalyticsPoint
        type="module"
        label="Articles section"
        typeLabel="article_listing"
        as={Container}
        fluid
      >
        <ArticleListingBody
          data={serverData.articleData}
          serverData={serverData.articleData}
          error={serverData.error}
          contextualFilters={serverData.contextualFilters}
          isFetching={false}
          defaultLoadedState
        />
      </AnalyticsPoint>
      {relatedListings.length > 0 && (
        <AnalyticsPoint
          type="module"
          label="Article subcategories"
          typeLabel="article_categories"
          as={Container}
          role="region"
          aria-label={`Categories related to ${node.title}`}
          className="bg-gray-100 py-5 px-0"
          fluid
        >
          <Container className="text-center text-sm-start mb-5">
            <h2 className="h2">{t('Related Categories')}</h2>
          </Container>
          <Filmstrip className="fs-offset">
            {relatedListings.map((listing: any, i: number) => {
              return (
                <AnalyticsPoint
                  key={i}
                  type="component"
                  node={{ ...listing, type: 'article_category' }}
                >
                  <Link
                    to={listing.path.alias}
                    className={`h4 btn btn-secondary py-4 px-5 flex-shrink-0 ${customButtonStyles}`}
                  >
                    {listing.title}
                  </Link>
                </AnalyticsPoint>
              );
            })}
          </Filmstrip>
        </AnalyticsPoint>
      )}
    </Layout>
  );
};

export const getServerData = async (props: any) => {
  try {
    const articleData = await getArticleSearchData(props.pageContext, props.query);
    return {
      status: 200,
      headers: {
        'Cache-Control': 'public, max-age=3600, s-maxage=3600, stale-while-revalidate=3600',
      },
      props: {
        articleData: articleData.data,
        contextualFilters: articleData.contextualFilters,
        error: null,
      },
    };
  } catch (e) {
    return {
      status: 500,
      props: {
        error: (e as AxiosError).response,
        articleData: null,
      },
    };
  }
};

export const query = graphql`
  query ($id: String!, $language: String!) {
    node: nodeArticleListingPage(id: { eq: $id }, langcode: { eq: $language }) {
      internal {
        type
      }
      id
      title
      path {
        alias
      }
      body {
        processed
      }
      langcode
      ...Metatag
      relationships {
        featuredArticle: field_article {
          ...ArticleCard
        }
        species: field_species_term {
          drupal_internal__tid
        }
        tags: field_tags {
          drupal_internal__tid
        }
        relatedListings: field_related_listings {
          id
          title
          path {
            alias
          }
          langcode
        }
      }
    }
    translations: allNodeArticleListingPage(
      filter: { id: { eq: $id }, langcode: { ne: $language } }
    ) {
      edges {
        node {
          langcode
          path {
            alias
          }
        }
      }
    }
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;

export default ArticleListingTemplate;
